<template>
    <div class="w-full xl:container xl:mx-auto px-4 py-8">
      <AssetsKitDump
        v-if="isDev"
        title="AssetsKit — Collection AXIS"
        :loading="akLoading"
        :error="akError"
        :api-routes="akDebugRoutes"
        :media-items="akDebugMediaItems"
      >
        <div v-if="akCollectionMeta" class="mt-2">
          <p class="text-gray-400 mb-1">── Collection: {{ akCollectionMeta.name || akCollectionMeta.slug || 'Unknown collection' }} ({{ akCollectionItems.length }} products) ──</p>
          <pre>{{ JSON.stringify(akCollectionDebugPayload, null, 2) }}</pre>
        </div>
        <template v-else-if="!akLoading">
          <p class="text-yellow-400 mb-2">Collection "axis" could not be normalized — raw response:</p>
          <pre>{{ JSON.stringify(akRawCollections, null, 2) }}</pre>
        </template>
      </AssetsKitDump>
      <AssetsKitSignatureCollection
        v-if="showAssetsKitPreview"
        :collection-name="akCollectionMeta.name"
        :collection-slug="akCollectionMeta.slug"
        :collection-slogan="akCollectionMeta.slogan"
        :collection-description="akCollectionMeta.description"
        :headline="contentProductTitle"
        :editorial-hook="t('editorialHook')"
        :display-order="displayColorOrder"
        :hero-color="selectedQueryColor || ''"
        :items="akCollectionItems"
        :fallback-cards="previewFallbackCards"
        :locale-prefix="localePrefix"
        product-type-path="/boots"
        :focus-price="variants[0]?.price"
      />

      <template v-else-if="showLegacyFallback">
        <h1 class="text-4xl font-light mb-3 text-center text-black">{{ t('title') }}</h1>
        <p class="text-center text-sm tracking-wide text-gray-500 italic mb-6">{{ t('editorialHook') }}</p>
        <p class="text-center text-gray-600 max-w-xl mx-auto mb-16">{{ t('intro') }}</p>
        <div class="product-grid grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 relative">
          <!-- Cross separator -->
          <div class="absolute inset-0 pointer-events-none">
            <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-px h-full bg-gray-200 hidden md:block"></div>
            <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-px w-full bg-gray-200 hidden md:block"></div>
          </div>
          <template v-for="(product, i) in variants" :key="`${product.color}-${i}`">
            <div class="flex flex-col items-center text-center">
              <NuxtLink :to="productLink(product.color)" class="block">
                <div class="relative overflow-hidden aspect-[4/5] bg-white">
                  <KImage
                    :src="product.image"
                    :alt="product.title"
                    class="w-full h-full object-contain"
                    width="1200"
                    height="1200"
                    :modifiers="{ fit: 'contain' }"
                    sizes="100vw md:50vw"
                    loading="eager"
                    :quality="85"
                  />
                  <div class="absolute inset-0 transition-all"></div>
                </div>
                <div class="mt-8">
                  <h3 class="text-3xl font-light mb-3 text-black">{{ product.title }}</h3>
                  <p class="mb-4 font-light text-xl text-black tracking-widest">{{ t(`colors.${product.color}`) }}</p>
                  <p class="text-xl text-black">{{ product.price }} €</p>
                </div>
              </NuxtLink>
            </div>
          </template>
        </div>
        <p class="mt-16 pt-8 border-t border-gray-200 text-center text-gray-600 text-sm max-w-2xl mx-auto">
          {{ $t('common.brandBlurb') }}
          <NuxtLink :to="localePrefix ? '/fr/brand' : '/brand'" class="text-black underline ml-1">{{ $t('common.brand') }}</NuxtLink>
        </p>
      </template>
      <ProductTechSpecs product="boots" />
      <ProductSeoBand product="boots" />
      <FrenchHeelsSeoBand />
      <EditorialCollectionCta />
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref } from 'vue'
import { useProductTypes } from '~/composables/useProductTypes'
import { fetchAssetsKitCollectionBySlug } from '~/utils/assetskitCollectionLookup'
import { buildAssetsKitProductSeoVariants, buildCollectionStructuredData, cleanProductSeoText } from '~/utils/assetskitProductSeo'
import { getStaticAssetsKitCollectionResponse } from '~/utils/assetskitStaticCollections'
import { buildProductColorPath, getProductTypeRouteSlug, normalizeColorRoute, toColorRouteSlug } from '~/utils/colorRoute'
import { getLocalePrefix } from '~/utils/locales'

const AK_PRODUCT_SPACE_ID = '052eeb97-9908-4b19-b5ba-6be2e922f2f8'
const AK_COLLECTION_SLUG = 'axis'
const SITE_URL = 'https://kendrick.paris'
const isDev = process.dev
const colorOrder = ['black'] as const
type ProductColorKey = typeof colorOrder[number]
type ProductVariantCard = {
  color: ProductColorKey
  image: string
  price: number
  title: string
  description: string
}
type LocalCollectionCopy = {
  colors: Record<string, string>
  editorialHook: string
  intro: string
  title: string
}

const savedColor = ref<ProductColorKey | null>(null)

function isProductColorKey(value: string): value is ProductColorKey {
  return (colorOrder as readonly string[]).includes(value)
}

const createDefaultAkResult = () => getStaticAssetsKitCollectionResponse(AK_PRODUCT_SPACE_ID, AK_COLLECTION_SLUG) || ({
  collection: null,
  rawLookup: null,
  lookupPayloads: [],
  routes: [],
  error: null
})
const hasStaticAkFallback = Boolean(createDefaultAkResult()?.collection)
const { t: i18nT } = useI18n()
const route = useRoute()
const currentLocale = useSiteLocale()
const localePrefix = computed(() => getLocalePrefix(currentLocale.value))
const isFrench = computed(() => currentLocale.value === 'fr')
const displayColorOrder = computed<string[]>(() => [...colorOrder])

const { data: akResult, pending: akLoading, refresh: refreshAkCollection } = useAsyncData(
  `assetskit-collection-${AK_COLLECTION_SLUG}-${currentLocale.value}`,
  () => fetchAssetsKitCollectionBySlug(AK_PRODUCT_SPACE_ID, AK_COLLECTION_SLUG, currentLocale.value),
  {
    lazy: import.meta.client,
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key] ?? nuxtApp.static.data[key],
    default: createDefaultAkResult,
    watch: [currentLocale]
  }
)

const akError = computed(() => akResult.value?.error || null)
const akCollection = computed(() => akResult.value?.collection || null)
const akRawCollections = computed(() => akResult.value?.rawLookup || null)
const akRoutes = computed(() => akResult.value?.routes || [])

const akCollectionMeta = computed(() => {
  if (!akCollection.value || akCollection.value.error) return null
  return akCollection.value.collection || (akCollection.value.id ? akCollection.value : null)
})

const akDebugRoutes = computed(() => {
  return akRoutes.value
})

const akCollectionItems = computed(() => {
  if (!akCollection.value || akCollection.value.error) return []
  if (Array.isArray(akCollection.value.items)) return akCollection.value.items
  if (Array.isArray(akCollection.value.products)) {
    return akCollection.value.products.map((product: any) => ({ products: product }))
  }
  return []
})

const showAssetsKitPreview = computed(() => !!akCollectionMeta.value && akCollectionItems.value.length > 0)
const showLegacyFallback = computed(() => !showAssetsKitPreview.value && !akLoading.value && !hasStaticAkFallback)
const previewFallbackCards = computed(() => showAssetsKitPreview.value ? [] : variants.value)

const akCollectionDebugPayload = computed(() => ({
  collection: {
    id: akCollectionMeta.value?.id,
    space_id: akCollectionMeta.value?.space_id,
    name: akCollectionMeta.value?.name,
    slug: akCollectionMeta.value?.slug,
    description: akCollectionMeta.value?.description,
    cover_image: akCollectionMeta.value?.cover_image,
    status: akCollectionMeta.value?.status,
    sort_order: akCollectionMeta.value?.sort_order,
    created_at: akCollectionMeta.value?.created_at,
    updated_at: akCollectionMeta.value?.updated_at,
    slogan: akCollectionMeta.value?.slogan
  },
  items: akCollectionItems.value.map((item: any) => ({
    id: item.products?.id,
    title: item.products?.title,
    slug: item.products?.slug,
    sku: item.products?.sku,
    price: item.products?.price_amount,
    variants: Array.isArray(item.products?.variants)
      ? item.products.variants.map((variant: any) => ({
          id: variant.id,
          title: variant.title,
          sku: variant.sku,
          option1: variant.option1,
          option2: variant.option2,
          option3: variant.option3,
          price_amount: variant.price_amount,
          inventory_quantity: variant.inventory_quantity,
          images: variant.images,
          photos: variant.photos,
          photos_zoom: variant.photos_zoom
        }))
      : []
  }))
}))

const akDebugMediaItems = computed(() => {
  const mediaMap = new Map<string, { label: string, url: string }>()

  for (const item of akCollectionItems.value) {
    const product = item?.products
    const productLabel = product?.slug || product?.title || 'product'
    const variants = Array.isArray(product?.variants) ? product.variants : []

    for (const variant of variants) {
      const variantLabel = [productLabel, variant?.option1, variant?.option2].filter(Boolean).join(' · ')
      const groups = [
        { label: `${variantLabel} · images`, urls: Array.isArray(variant?.images) ? variant.images : [] },
        { label: `${variantLabel} · photos`, urls: Array.isArray(variant?.photos) ? variant.photos : [] },
        { label: `${variantLabel} · zoom`, urls: Array.isArray(variant?.photos_zoom) ? variant.photos_zoom : [] }
      ]

      for (const group of groups) {
        for (const url of group.urls) {
          if (!url || mediaMap.has(url)) continue
          mediaMap.set(url, { label: group.label, url })
        }
      }
    }
  }

  return Array.from(mediaMap.values())
})

const selectedQueryColor = computed(() => {
  const rawColor = Array.isArray(route.params.color)
    ? route.params.color[0]
    : route.params.color || (Array.isArray(route.query.color) ? route.query.color[0] : route.query.color)
  if (typeof rawColor !== 'string') return null
  const normalizedColor = normalizeColorRoute(rawColor)
  return isProductColorKey(normalizedColor) ? normalizedColor : null
})

const productLink = (color: string) => buildProductColorPath('/boots', color, localePrefix.value)

// Static translations
const translations: Record<'fr' | 'en', LocalCollectionCopy> = {
  fr: {
    title: 'AXIS 110 Botte',
    editorialHook: 'L\'architecture commence à la cheville.',
    intro: 'Présence architecturale construite autour d\'un talon structurel de 110mm. Conçu pour repositionner votre posture avant chaque pas.',
    colors: {
      yellow: 'Safran',
      black: 'Noir',
      red: 'Carmin',
      beige: 'Ivoire'
    }
  },
  en: {
    title: 'AXIS 110 Boot',
    editorialHook: 'Architecture begins at the ankle.',
    intro: 'Architectural presence built around a 110mm structured heel. Designed to reposition your stance before you take a step.',
    colors: {
      yellow: 'Saffron',
      black: 'Black',
      red: 'Carmine',
      beige: 'Ivoire'
    }
  }
}

const t = (key: string): string => {
  const keys = key.split('.')
  const localeCopy = currentLocale.value === 'fr'
    ? translations.fr
    : currentLocale.value === 'en'
      ? translations.en
      : null

  if (localeCopy) {
    let value: unknown = localeCopy

    for (const k of keys) {
      value = value && typeof value === 'object' && k in value
        ? (value as Record<string, unknown>)[k]
        : undefined
    }

    if (typeof value === 'string') return value
  }

  if (key.startsWith('colors.')) return i18nT(key)
  if (key === 'editorialHook') return i18nT('editorialHook.boots')
  if (key === 'title') return i18nT('products.boots')
  return key
}

const contentProductTitle = computed(() => (
  cleanProductSeoText(akCollectionItems.value[0]?.products?.title) || t('title')
))

const { fetchProductTypes, getProductTypePrice } = useProductTypes()

const baseVariants = [
  {
    color: 'black',
    title: 'AXIS 110 Boot',
    image: '/product/boots/kendrick-boots-black-1_.jpg',
    description: 'Discover the epitome of elegance with our Kendrick Boots, meticulously crafted from supple lambskin for an unparalleled soft touch.'
  }
] satisfies Array<Omit<ProductVariantCard, 'price'>>

const variants = computed<ProductVariantCard[]>(() => {
  console.log('Boots index: Computing variants...')
  
  // Get price from database
  const dbPrice = getProductTypePrice.value('boots')
  const price = dbPrice && dbPrice > 0 ? dbPrice : 980
  // Use locale for product title (FR: AXIS 110 Botte, EN: AXIS 110 Boot)
  const productTitle = contentProductTitle.value

  // Map baseVariants to use database price and locale-aware title
  const variantsWithDbPrice = baseVariants.map(variant => ({
    ...variant,
    title: productTitle,
    price
  }))
  const orderedByCampaign = colorOrder
    .map(color => variantsWithDbPrice.find(product => product.color === color))
    .filter((product): product is ProductVariantCard => Boolean(product))

  const activeColor = selectedQueryColor.value || savedColor.value

  console.log('Boots index: Saved color from state:', activeColor)
  if (activeColor) {
    const savedIndex = colorOrder.indexOf(activeColor)
    const reorderedColors: ProductVariantCard[] = []

    for (let i = 0; i < colorOrder.length; i++) {
      const colorIndex = (savedIndex + i) % colorOrder.length
      const color = colorOrder[colorIndex]
      const product = variantsWithDbPrice.find(p => p.color === color)
      if (product) {
        reorderedColors.push(product)
      }
    }

    console.log('Boots index: Reorganizing with saved color first:', activeColor)
    console.log('Boots index: Final order:', reorderedColors.map(p => p.color))
    return reorderedColors
  }

  // Always return the campaign order when no color was selected yet.
  console.log('Boots index: Using default order:', orderedByCampaign.map(p => p.color))
  return orderedByCampaign
})

const priceValidUntil = computed(() => new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10))
const baseCanonicalUrl = computed(() => `${SITE_URL}${localePrefix.value}/${getProductTypeRouteSlug('boots', localePrefix.value)}`)
const selectedColorSlug = computed(() => selectedQueryColor.value ? toColorRouteSlug(selectedQueryColor.value) : null)
const canonicalUrl = computed(() => selectedColorSlug.value ? `${baseCanonicalUrl.value}/${selectedColorSlug.value}` : baseCanonicalUrl.value)
const alternateEnUrl = computed(() => selectedColorSlug.value ? `${SITE_URL}/boots/${selectedColorSlug.value}` : `${SITE_URL}/boots`)
const alternateFrUrl = computed(() => selectedColorSlug.value ? `${SITE_URL}/fr/bottes/${selectedColorSlug.value}` : `${SITE_URL}/fr/bottes`)
const baseSeoTitle = computed(() => (
  isFrench.value
    ? 'Bottes à talon aiguille de créateur — AXIS 110 | Kendrick Paris'
    : 'Designer Boots Paris — AXIS 110 | Kendrick Paris'
))
const baseSeoDescription = computed(() => (
  isFrench.value
    ? 'Botte à talon aiguille de créateur : talon 110 mm, cuir d\'agneau italien, assemblée à la main en Italie. Noir, production limitée. 980 €.'
    : 'Full-height boot in Italian lambskin. 110mm heel. Hand-assembled in Marche, Italy. Limited production. €980.'
))
const baseSeoKeywords = computed(() => (
  isFrench.value
    ? 'bottes à talon aiguille, bottes à talon créateur, bottes talon haut, bottes noires créateur, bottes cuir créateur paris, Kendrick Paris'
    : 'designer boots paris, french designer boots, luxury boots, italian lambskin boots, kendrick paris, axis 110'
))
const collectionSeoDescription = computed(() => cleanProductSeoText(baseSeoDescription.value) || cleanProductSeoText(akCollectionMeta.value?.description))
const seoVariants = computed(() => buildAssetsKitProductSeoVariants({
  items: akCollectionItems.value,
  fallbackCards: previewFallbackCards.value,
  colorOrder: displayColorOrder.value,
  baseUrl: baseCanonicalUrl.value,
  defaultTitle: contentProductTitle.value,
  defaultDescription: collectionSeoDescription.value,
  colorLabel: color => t(`colors.${color}`),
  siteUrl: SITE_URL
}))
const selectedSeoVariant = computed(() => (
  selectedQueryColor.value
    ? seoVariants.value.find(variant => variant.color === selectedQueryColor.value) || null
    : null
))
const metaTitle = computed(() => {
  if (!selectedSeoVariant.value) return baseSeoTitle.value
  if (isFrench.value) return `Botte à talon aiguille AXIS 110 — ${t(`colors.${selectedSeoVariant.value.color}`)} | Kendrick Paris`
  return `${selectedSeoVariant.value.title} | Kendrick Paris`
})
const metaDescription = computed(() => selectedSeoVariant.value?.description || collectionSeoDescription.value)
const metaImage = computed(() => selectedSeoVariant.value?.image || seoVariants.value[0]?.image || `${SITE_URL}/product/boots/kendrick-boots-black-1_.jpg`)
const structuredData = computed(() => buildCollectionStructuredData({
  collectionId: akCollectionMeta.value?.id || AK_COLLECTION_SLUG,
  collectionName: contentProductTitle.value,
  collectionDescription: collectionSeoDescription.value,
  selectedVariant: selectedSeoVariant.value,
  variants: seoVariants.value,
  category: isFrench.value ? 'Bottes' : 'Boots',
  priceValidUntil: priceValidUntil.value
}))

// Fetch product types on mount to get pricing from database
onMounted(() => {
  void refreshAkCollection()
  fetchProductTypes()

  if (selectedQueryColor.value) {
    savedColor.value = selectedQueryColor.value
    sessionStorage.setItem('selectedProductColor', selectedQueryColor.value)
    return
  }

  const storedColor = sessionStorage.getItem('selectedProductColor')
  if (storedColor && isProductColorKey(storedColor)) {
    savedColor.value = storedColor
  }
})

useShareImage('/k/boots/kendrick-boots-black-1.jpg', 'Kendrick Paris black leather boots')

// Set page title and SEO
useHead(() => ({
  title: metaTitle.value,
  meta: [
    { name: 'description', content: metaDescription.value },
    { name: 'keywords', content: baseSeoKeywords.value },
    { name: 'robots', content: 'index, follow' },
    { property: 'og:title', content: metaTitle.value },
    { property: 'og:description', content: metaDescription.value },
    { property: 'og:type', content: selectedSeoVariant.value ? 'product' : 'website' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:image', content: metaImage.value },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: metaTitle.value },
    { name: 'twitter:description', content: metaDescription.value },
    { name: 'twitter:image', content: metaImage.value }
  ],
  link: [
    { rel: 'canonical', href: canonicalUrl.value },
    { rel: 'alternate', hreflang: 'en', href: alternateEnUrl.value },
    { rel: 'alternate', hreflang: 'fr', href: alternateFrUrl.value },
    { rel: 'alternate', hreflang: 'x-default', href: alternateEnUrl.value }
  ],
  script: structuredData.value
    ? [
        {
          key: 'collection-product-structured-data',
          type: 'application/ld+json',
          innerHTML: JSON.stringify(structuredData.value)
        }
      ]
    : []
}))
</script> 

<style scoped>
/* editorial — utility-only page */
</style>
